import { createContext, useContext, useId, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface FieldContextValue {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Inputs call this to pick up id / aria-describedby / aria-invalid from the surrounding Field. */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Override the generated id (must match the control's id). */
  id?: string;
  /** Something small rendered to the right of the label, e.g. a "Clear" link. */
  labelAction?: ReactNode;
  className?: string;
  children: ReactNode;
}

export const labelClasses = 'block text-sm font-medium text-gray-700 dark:text-gray-300';

/**
 * Label + control + hint/error with the wiring screen readers need. Wrap any control:
 *   <Field label="Name" required error={errors.name}><Input value=… /></Field>
 * Input/Select/Textarea also accept `label` and wrap themselves.
 */
export function Field({ label, hint, error, required, id: idProp, labelAction, className, children }: FieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between gap-2">
        {/* The required mark is CSS-only so it never leaks into the label text / accessible name. */}
        <label htmlFor={id} className={cn(labelClasses, required && "after:ml-0.5 after:text-red-500 after:content-['*']")}>
          {label}
        </label>
        {labelAction}
      </div>
      <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>{children}</FieldContext.Provider>
      {hint && (
        <p id={hintId} className="text-xs text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
